import {
  type ForkTemplateSchema,
  forkTemplateSchema,
} from "@gearbox-protocol/deploy-tools-shared";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FormEvent } from "react";
import { useCallback, useState } from "react";
import type { FileWithPath } from "react-dropzone";
import type { UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useAsyncFn, useList } from "react-use";
import { parse, stringify } from "yaml";
import { prettifyError, ZodError } from "zod/v4";
import {
  type ForkSchema,
  forkSchema,
  fromForkTemplate,
  toForkTemplate,
} from "./schema";

export type EditMode = "form" | "raw";

export interface UseForkFormProps {
  defaultValues: ForkTemplateSchema;
  defaultFiles?: FileWithPath[];
  onSubmit: (data: ForkTemplateSchema, files: FileWithPath[]) => Promise<void>;
}

export interface UseForkFormReturn {
  form: UseFormReturn<ForkSchema>;
  code: string;
  error: string;
  setCode: (code: string) => void;
  mode: EditMode;
  setMode: (mode: EditMode) => void;
  submit: (e: FormEvent) => void;

  files: FileWithPath[];
  setFiles: (files: File[]) => void;
}

export function useForkForm(props: UseForkFormProps): UseForkFormReturn {
  const { defaultValues, defaultFiles = [], onSubmit } = props;
  const [mode, changeEditMode] = useState<EditMode>("form");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [files, { set: setFiles }] = useList<FileWithPath>(defaultFiles);

  const form = useForm<ForkSchema>({
    resolver: zodResolver(forkSchema),
    defaultValues: fromForkTemplate(defaultValues),
  });

  const [_, setMode] = useAsyncFn(
    async (newMode: EditMode) => {
      if (newMode === "form") {
        // when switching from raw to form
        // validate yaml
        // if not correct, prevent switching
        try {
          const obj = parse(code);
          const newFormTemplate = forkTemplateSchema.parse(obj);
          form.reset(fromForkTemplate(newFormTemplate));
          setError("");
          changeEditMode("form");
        } catch (e) {
          if (e instanceof ZodError) {
            setError(prettifyError(e));
          } else {
            setError(`${e}`);
          }
        }
      } else {
        // when switching from form to raw
        // validate form, if not valid - prevent switching
        const valid = await form.trigger();
        if (valid) {
          setError("");
          changeEditMode("raw");
          // to clean unused options
          const input = forkSchema.parse(form.getValues());
          const template = toForkTemplate(input);
          setCode(stringify(template));
        }
      }
    },
    [changeEditMode, setCode, code, form, setError],
  );

  const submit = useCallback(
    (e: FormEvent) => {
      setError("");
      // Try to update form data from yaml
      if (mode === "raw") {
        try {
          const obj = parse(code);
          const newFormTemplate = forkTemplateSchema.parse(obj);
          form.reset(fromForkTemplate(newFormTemplate));
        } catch (err) {
          if (err instanceof ZodError) {
            setError(prettifyError(err));
          } else {
            setError(`${err}`);
          }
          e.preventDefault();
          return;
        }
      }

      form.handleSubmit(async (data: ForkSchema) => {
        try {
          await onSubmit(toForkTemplate(data), files);
        } catch (err) {
          setError(`${err}`);
          e.preventDefault();
        }
      })(e);
    },
    [form, mode, code, onSubmit, files],
  );

  return {
    form,
    code,
    error,
    setCode,
    mode,
    setMode,
    submit,

    files,
    setFiles,
  };
}
